const DAY_MS = 24 * 60 * 60 * 1000

function parseIsoDate(isoDate: string): { year: number; month: number; day: number } | null {
  const m = String(isoDate).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return null
  const year = Number(m[1])
  const month = Number(m[2])
  const day = Number(m[3])
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  return { year, month, day }
}

function isoPartsForTimeZone(utcDate: Date, timeZone: string): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(utcDate)

  const byType: Record<string, string> = {}
  for (const p of parts) byType[p.type] = p.value
  return {
    year: Number(byType.year),
    month: Number(byType.month),
    day: Number(byType.day),
    hour: Number(byType.hour),
    minute: Number(byType.minute),
    second: Number(byType.second),
  }
}

function getTimeZoneOffsetMs(utcDate: Date, timeZone: string): number {
  const parts = isoPartsForTimeZone(utcDate, timeZone)
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second, 0)
  return asUtc - utcDate.getTime()
}

export function zonedDateTimeToUtc(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second: number,
  millisecond: number,
  timeZone: string
): Date {
  let guess = Date.UTC(year, month - 1, day, hour, minute, second, millisecond)
  for (let i = 0; i < 3; i++) {
    const offset = getTimeZoneOffsetMs(new Date(guess), timeZone)
    const adjusted = Date.UTC(year, month - 1, day, hour, minute, second, millisecond) - offset
    if (adjusted === guess) break
    guess = adjusted
  }
  return new Date(guess)
}

export function toIsoDateUtc(input: Date): string {
  return new Date(input).toISOString().slice(0, 10)
}

/**
 * Converts an ISO date string (YYYY-MM-DD) to a UTC-midnight Date.
 * IMPORTANT: This is a label convention, NOT the actual PKT day-start timestamp.
 * When the input is a PKT business-date label (e.g. "2026-03-15"), the output
 * YYYY-MM-DDT00:00:00.000Z is used as a stored label for that PKT day. The actual
 * PKT day start in UTC would be the previous calendar day (e.g. 2026-03-14T19:00:00.000Z).
 */
export function isoDateToUtcLabelDate(isoDate: string): Date {
  return new Date(`${isoDate}T00:00:00.000Z`)
}

export function addDaysToIsoDate(isoDate: string, days: number): string {
  const base = parseIsoDate(isoDate)
  if (!base) throw new Error(`Invalid ISO date: ${isoDate}`)
  const utc = Date.UTC(base.year, base.month - 1, base.day + days, 0, 0, 0, 0)
  return new Date(utc).toISOString().slice(0, 10)
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) {
    const isLeap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
    return isLeap ? 29 : 28
  }
  if ([4, 6, 9, 11].includes(month)) return 30
  return 31
}

/**
 * Pure calendar month arithmetic on ISO date strings. No timezone dependency.
 * Clamps day when target month has fewer days (e.g. 2025-03-31 - 1 month = 2025-02-28).
 */
export function addMonthsToIsoDate(isoDate: string, months: number): string {
  const base = parseIsoDate(isoDate)
  if (!base) throw new Error(`Invalid ISO date: ${isoDate}`)
  let month = base.month + months
  let year = base.year
  while (month > 12) {
    month -= 12
    year += 1
  }
  while (month < 1) {
    month += 12
    year -= 1
  }
  const maxDay = daysInMonth(year, month)
  const day = Math.min(base.day, maxDay)
  const mm = String(month).padStart(2, '0')
  const dd = String(day).padStart(2, '0')
  return `${year}-${mm}-${dd}`
}

export function diffIsoDays(fromIso: string, toIso: string): number {
  const from = parseIsoDate(fromIso)
  const to = parseIsoDate(toIso)
  if (!from || !to) throw new Error(`Invalid ISO day range: ${fromIso}..${toIso}`)
  const fromUtc = Date.UTC(from.year, from.month - 1, from.day, 0, 0, 0, 0)
  const toUtc = Date.UTC(to.year, to.month - 1, to.day, 0, 0, 0, 0)
  return Math.floor((toUtc - fromUtc) / DAY_MS)
}

export function eachIsoDateInclusive(fromIso: string, toIso: string): string[] {
  const diff = diffIsoDays(fromIso, toIso)
  if (diff < 0) return []
  const dates: string[] = []
  for (let i = 0; i <= diff; i++) dates.push(addDaysToIsoDate(fromIso, i))
  return dates
}

export function getTimeZoneDayBoundsUtc(isoDate: string, timeZone: string): { startUtc: Date; endUtc: Date } {
  const parsed = parseIsoDate(isoDate)
  if (!parsed) throw new Error(`Invalid ISO date: ${isoDate}`)
  const startUtc = zonedDateTimeToUtc(parsed.year, parsed.month, parsed.day, 0, 0, 0, 0, timeZone)
  const nextIso = addDaysToIsoDate(isoDate, 1)
  const next = parseIsoDate(nextIso)
  if (!next) throw new Error(`Invalid next ISO date: ${nextIso}`)
  const nextStartUtc = zonedDateTimeToUtc(next.year, next.month, next.day, 0, 0, 0, 0, timeZone)
  return {
    startUtc,
    endUtc: new Date(nextStartUtc.getTime() - 1),
  }
}
